import React, { useState, useMemo } from 'react';

const TYPE_COLORS = {
    A: '#3b82f6',
    AAAA: '#8b5cf6',
    CNAME: '#f38020',
    MX: '#10b981',
    TXT: '#f59e0b',
    NS: '#6b7280',
};

const SubdomainTree = ({ records, zone, onEditRecord, onDeleteRecord }) => {
    const groups = useMemo(() => {
        const map = {};
        (records || []).forEach(record => {
            let group;
            const zoneName = zone?.name || '';
            if (record.name === zoneName) {
                group = '@';
            } else if (record.name.endsWith('.' + zoneName)) {
                group = record.name.slice(0, -(zoneName.length + 1));
            } else {
                group = record.name;
            }
            if (!map[group]) map[group] = [];
            map[group].push(record);
        });

        const sorted = Object.keys(map).sort((a, b) => {
            if (a === '@') return -1;
            if (b === '@') return 1;
            return a.localeCompare(b);
        });

        return sorted.map(name => ({
            name,
            records: map[name],
            types: [...new Set(map[name].map(r => r.type))],
        }));
    }, [records, zone]);

    const [expandedGroups, setExpandedGroups] = useState(() => {
        if (groups.length < 15) {
            return new Set(groups.map(g => g.name));
        }
        return new Set(['@']);
    });

    const toggleGroup = (name) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    };

    const formatTTL = (ttl) => {
        if (ttl === 1) return 'Auto';
        return `${ttl}s`;
    };

    return (
        <div className="subdomain-tree">
            {groups.map(group => {
                const expanded = expandedGroups.has(group.name);
                return (
                    <div className="subdomain-group" key={group.name}>
                        <div
                            className="subdomain-group-header"
                            onClick={() => toggleGroup(group.name)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', userSelect: 'none' }}
                        >
                            <span style={{ fontSize: '12px', width: '16px', display: 'inline-block' }}>
                                {expanded ? '\u25BC' : '\u25B6'}
                            </span>
                            <span className="subdomain-group-name" style={{ fontWeight: 600, fontSize: '14px' }}>
                                {group.name === '@' ? '(raiz)' : group.name}
                            </span>
                            <span className="subdomain-badge" style={{
                                backgroundColor: '#e5e7eb',
                                color: '#374151',
                                borderRadius: '10px',
                                padding: '1px 8px',
                                fontSize: '12px',
                                fontWeight: 500,
                            }}>
                                {group.records.length}
                            </span>
                            <span style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                                {group.types.map(type => (
                                    <span key={type} style={{
                                        backgroundColor: TYPE_COLORS[type] || '#6b7280',
                                        color: '#fff',
                                        borderRadius: '8px',
                                        padding: '1px 7px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                    }}>
                                        {type}
                                    </span>
                                ))}
                            </span>
                        </div>
                        {expanded && (
                            <div className="subdomain-group-content" style={{ padding: '0' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                                            <th style={{ padding: '6px 12px', fontWeight: 600 }}>Tipo</th>
                                            <th style={{ padding: '6px 12px', fontWeight: 600 }}>Conteudo</th>
                                            <th style={{ padding: '6px 12px', fontWeight: 600 }}>TTL</th>
                                            <th style={{ padding: '6px 12px', fontWeight: 600 }}>Proxy</th>
                                            <th style={{ padding: '6px 12px', fontWeight: 600 }}>Acoes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.records.map(record => (
                                            <tr key={record.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                <td style={{ padding: '6px 12px' }}>
                                                    <span style={{
                                                        backgroundColor: TYPE_COLORS[record.type] || '#6b7280',
                                                        color: '#fff',
                                                        borderRadius: '8px',
                                                        padding: '2px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                    }}>
                                                        {record.type}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '6px 12px', wordBreak: 'break-all', maxWidth: '400px' }}>
                                                    {record.content}
                                                </td>
                                                <td style={{ padding: '6px 12px' }}>
                                                    {formatTTL(record.ttl)}
                                                </td>
                                                <td style={{ padding: '6px 12px' }}>
                                                    {record.proxied ? '\u2713' : '\u2014'}
                                                </td>
                                                <td style={{ padding: '6px 12px', display: 'flex', gap: '4px' }}>
                                                    <button
                                                        className="btn btn-secondary"
                                                        style={{ padding: '3px 8px', fontSize: '12px' }}
                                                        onClick={() => onEditRecord(record)}
                                                        title="Editar"
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        className="btn"
                                                        style={{ padding: '3px 8px', fontSize: '12px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                        onClick={() => onDeleteRecord(record)}
                                                        title="Deletar"
                                                    >
                                                        Deletar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SubdomainTree;
